import compose from 'lodash/fp/compose';
import pick from 'lodash/fp/pick';
import unionBy from 'lodash/unionBy';
import uniq from 'lodash/uniq';

import {
  canAdd,
  canPublish,
  canUpdate,
  hasReferences,
  paginate,
  searchable,
  selectable,
  uploadable
} from '../plugins';
import challengesApi from '../../firebase/challenges';
import PluggableModule from '../pluggable';
import topicsApi from '../../firebase/topics';

const challenges = new PluggableModule('challenge');

const ucName = challenges.name.toUpperCase();
const UPDATE_HINT = `${ucName}_UPDATE_HINT`;
const UPDATE_SOLUTION = `${ucName}_UPDATE_SOLUTION`;
const UPDATE_TOPICS_INIT = `${ucName}_UPDATE_TOPICS_INIT`;
const UPDATE_TOPICS_ERROR = `${ucName}_UPDATE_TOPICS_ERROR`;
const UPDATE_TOPICS_DONE = `${ucName}_UPDATE_TOPICS_DONE`;

challenges.addMutationTypes([
  UPDATE_HINT,
  UPDATE_SOLUTION,
  UPDATE_TOPICS_INIT,
  UPDATE_TOPICS_ERROR,
  UPDATE_TOPICS_DONE
]);

challenges.extend({
  namespaced: true,
  state: {
    updatingRelatedTopics: false,
    updatingRelatedTopicsError: null
  },
  mutations: {
    [UPDATE_HINT](state, hintQuestionId) {
      state.selected = {
        ...state.selected,
        details: {
          ...state.selected.details,
          hintQuestionId
        }
      };
    },
    [UPDATE_SOLUTION](state, solution) {
      state.selected = {
        ...state.selected,
        details: {
          ...state.selected.details,
          solution
        }
      };
    },
    [UPDATE_TOPICS_INIT](state) {
      state.updatingRelatedTopics = true;
      state.updatingRelatedTopicsError = null;
    },
    [UPDATE_TOPICS_ERROR](state, error) {
      state.updatingRelatedTopicsError = error;
      state.updatingRelatedTopics = false;
    },
    [UPDATE_TOPICS_DONE](state) {
      state.updatingRelatedTopics = false;
    }
  },
  actions: {
    async addChallenge({ commit }, data) {
      commit(challenges.con.CHALLENGE_SUBMIT_INIT);
      try {
        const newChallengeRef = await challengesApi.add(data);
        commit(challenges.con.CHALLENGE_CREATED, newChallengeRef.id);
        commit(challenges.con.CHALLENGE_SUBMIT_DONE);
      } catch (error) {
        commit(challenges.con.CHALLENGE_SUBMIT_DONE);
        throw error;
      }
    },
    async publishChallenge({ commit, state }, data) {
      commit(challenges.con.CHALLENGE_PUBLISH_INIT);
      try {
        await challengesApi.publish(state.selected.id, data.publishLevel);
        // commit(challenges.con.CHALLENGE_UPDATE_FIELDS, data);
        commit(challenges.con.CHALLENGE_PUBLISH_DONE);
      } catch (error) {
        commit(challenges.con.CHALLENGE_PUBLISH_DONE);
        throw error;
      }
    },
    toggleHint({ commit, state }, id) {
      commit(
        UPDATE_HINT,
        state.selected.details.hintQuestionId === id ? null : id
      );
    },
    updateSolution({ commit }, solutionUrl) {
      commit(UPDATE_SOLUTION, solutionUrl);
    },
    async updateChallenge({ commit }, data) {
      commit(challenges.con.CHALLENGE_SUBMIT_INIT);
      try {
        await challengesApi.update(data);
        commit(challenges.con.CHALLENGE_SUBMIT_DONE);
      } catch (error) {
        commit(challenges.con.CHALLENGE_SUBMIT_DONE);
        throw error;
      }
    },
    async updateRelatedTopics({ commit, state }) {
      commit(UPDATE_TOPICS_INIT);
      try {
        const topicIds = uniq(
          state.selected.details.relatedConcepts.map(o => o.topicId)
        );
        const topics = await topicsApi.getAll(topicIds);
        state.selected.details.relatedTopics = unionBy(
          state.selected.details.relatedTopics,
          topics.map(pick(['id', 'name'])),
          o => o.id
        );
        commit(UPDATE_TOPICS_DONE);
      } catch (error) {
        commit(UPDATE_TOPICS_ERROR, error);
      }
    }
  }
});

const hop = compose(
  canAdd(),
  canPublish(),
  canUpdate(),
  paginate('challenges'),
  selectable(challengesApi, true),
  searchable(),
  hasReferences(
    {
      collection: 'questions',
      field: 'questions',
      parent: ['challenges', ['data', 'details']]
    },
    challengesApi
  ),
  uploadable('solution')
);

export default hop(challenges);
